import { Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"

export interface FiltersToggleButtonProps {
  isOpen: boolean
  /** Shown as a badge so the collapsed button still reports what is filtering. */
  activeCount: number
  onToggle: () => void
  /** `id` of the collapsible panel this button controls. */
  panelId: string
  className?: string
}

/** Toolbar control that collapses a facet filter row — shared by Opportunities and the Opportunity Hub. */
export function FiltersToggleButton({
  isOpen,
  activeCount,
  onToggle,
  panelId,
  className,
}: FiltersToggleButtonProps) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={panelId}
      className={cn(
        "flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors sm:h-8",
        activeCount > 0
          ? "border-primary/40 bg-primary/5 text-foreground dark:border-primary/60 dark:bg-primary/15 dark:text-primary"
          : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      <Icon name="filter" size="xs" />
      {t("common.filters")}
      {activeCount > 0 && (
        <span
          data-testid="filter-count-badge"
          className="flex size-4 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold tabular-nums text-primary"
        >
          {activeCount}
        </span>
      )}
      <Icon
        name={isOpen ? "chevron-up" : "chevron-down"}
        size="xs"
        className="ml-0.5 text-muted-foreground/60"
      />
    </button>
  )
}
